import { useProject, useSession } from "@/contexts/Auth";
import { useMintUserSessionMutation } from "@gram/client/react-query/mintUserSession.js";
import type { MintUserSessionRequestBody } from "@gram/client/models/components/mintusersessionrequestbody.js";
import { useQuery } from "@tanstack/react-query";

/**
 * The resource a minted JWT's audience binds to. The unified mint RPC takes
 * exactly one of these:
 *
 * - `toolset` binds to the toolset's `/mcp/{slug}` surface.
 * - `mcpServer` binds to the server's user_session_issuer — proxied MCP
 *   servers are mcp_servers-backed and carry no toolset.
 */
export type UserSessionTokenTarget =
  | { kind: "toolset"; id: string | undefined }
  | { kind: "mcpServer"; id: string | undefined }
  | {
      kind: "metaMcpServer";
      id: string | undefined;
      discoveryMode?: "direct" | "progressive" | "code_mode";
      connectionVersion?: string;
    };

export interface UseUserSessionTokenResult {
  /** The minted user-session JWT, or undefined while loading / not gated. */
  accessToken: string | undefined;
  isLoading: boolean;
  /** Whether minting failed (when the caller handles errors inline). */
  isError: boolean;
  error: Error | null;
  /** Retry minting only when the target is issuer-gated and available. */
  refetch: () => void;
}

function mintRequestBody(
  target: UserSessionTokenTarget,
): MintUserSessionRequestBody {
  switch (target.kind) {
    case "toolset":
      return { toolsetId: target.id };
    case "mcpServer":
      return { mcpServerId: target.id };
    case "metaMcpServer":
      return {
        metaMcpServerId: target.id,
        discoveryMode: target.discoveryMode,
      };
  }
}

/**
 * Mints a user-session JWT scoped to a toolset or a proxied MCP server. The
 * minted token is what the dashboard's MCP clients send as
 * `Authorization: Bearer` on the target's MCP surface, so the runtime gateway
 * resolves the dashboard user's stored upstream credentials through the same
 * path a real MCP client would after an OAuth dance — for proxied MCP servers
 * it matches what /mcp/{slug}/token would emit.
 *
 * Only issuer-gated targets get a token; the mint RPC 400s for the rest, so a
 * target with no `userSessionIssuerId` leaves `accessToken` undefined and lets
 * the connection proceed unauthenticated.
 *
 * The issuer id is part of the query key, not just the gate: the minted JWT's
 * audience binds to the issuer, so relinking a target to a different issuer has
 * to invalidate the cached token. Keying on the target alone would keep serving
 * a JWT bound to the old issuer for the full 45-minute staleTime, and the
 * gateway would reject its audience until the entry expired.
 *
 * Consumers sharing a target share the query key, so React Query dedupes the
 * mint across them — e.g. the playground's auth panel and chat surface issue a
 * single request between them.
 */
export function useUserSessionToken({
  target,
  userSessionIssuerId,
  project: projectOverride,
  throwOnError,
}: {
  target: UserSessionTokenTarget;
  userSessionIssuerId: string | undefined;
  /**
   * The target's project, for org-level pages that have no ambient project
   * (the role editor lists servers from every project).
   */
  project?: { id: string; slug: string };
  /** Opt out of the QueryClient error boundary for inline error handling. */
  throwOnError?: false;
}): UseUserSessionTokenResult {
  const session = useSession();
  const ambientProject = useProject();
  const project = projectOverride ?? ambientProject;
  const mintMutation = useMintUserSessionMutation();

  const { kind, id } = target;
  const isIssuerGated = !!userSessionIssuerId;
  const enabled = !!id && isIssuerGated;

  const query = useQuery({
    queryKey: [
      "userSessionToken",
      kind,
      project.id,
      id,
      userSessionIssuerId,
      session.user.id,
      ...(target.kind === "metaMcpServer"
        ? [target.discoveryMode, target.connectionVersion]
        : []),
    ],
    queryFn: async () => {
      if (!enabled) return null;
      const result = await mintMutation.mutateAsync({
        request: {
          gramProject: project.id,
          mintUserSessionRequestBody: mintRequestBody(target),
        },
        security: {
          sessionHeaderGramSession: session.session,
          projectSlugHeaderGramProject: project.slug,
        },
      });
      return result;
    },
    enabled,
    // The minted JWT is good for ~1h; refresh well before expiry so the
    // connection always has headroom.
    staleTime: 1000 * 60 * 45,
    refetchInterval: 1000 * 60 * 45,
    refetchOnWindowFocus: false,
    retry: false,
    ...(throwOnError === false ? { throwOnError: false } : {}),
  });

  return {
    // Gate on `enabled` so a non-issuer-gated target never surfaces a token
    // (stale or otherwise) that would get attached to an unauthenticated
    // connection.
    accessToken: enabled ? query.data?.accessToken : undefined,
    isLoading:
      enabled && (query.isLoading || (query.isError && query.isFetching)),
    isError: enabled && query.isError,
    error: enabled ? query.error : null,
    refetch: () => {
      if (enabled) void query.refetch({ cancelRefetch: false });
    },
  };
}
